<template>
  <div v-if="renderReady && !model.$disabled" class="datepicker">
    <label v-if="getTitle || tooltipContent" class="label" :for="sid">
      <span>{{ getTitle }}</span>

      <w-tooltip v-if="tooltipContent">{{ tooltipContent }}</w-tooltip>

      <small class="ml-1" aria-hidden="true">(Datę należy wprowadzić w formacie dzień.miesiąc.rok)</small>

      <small class="sr-only">
        Datę należy wprowadzić w formacie dzień kropka miesiąc kropka rok
      </small>
    </label>

    <label v-else class="sr-only" :for="sid">
      <span>{{ titleAttribute || model.$schema.title }}</span>

      Datę należy wprowadzić w formacie dzień kropka miesiąc kropka rok
    </label>

    <div class="input input-group" :class="{ 'state-error': model.$error, 'state-disabled': disabled && !borderless }">
      <input
        :id="sid"
        ref="input"
        v-model="model.$value"
        type="text"
        class="form-control"
        autocomplete="off"
        :disabled="disabled"
        :class="{ borderless: borderless, ...borderValidationClass }"
        :placeholder="datepicker.options.placeholder"
        :name="model.$form.testPath ? model.$path : undefined"
        :title="titleAttribute || model.$schema.title"
        :aria-describedby="errorVisible ? errId : undefined"
        :aria-invalid="errorVisible"
        @blur="onBlur"
      />

      <div v-if="!disabled" class="input-group-append">
        <button v-if="model.$value" aria-label="Wyczyść pole" class="btn btn-outline-dark" @click.prevent="clear">
          <i aria-hidden="true" class="fal fa fa-times" />
        </button>

        <button class="btn btn-outline-dark" tabindex="-1" aria-hidden="true" @click.prevent="toggleDatePicker">
          <i class="fal fa fa-calendar" />
        </button>

        <button
          v-if="todayButton"
          class="btn btn-outline-dark"
          tabindex="-1"
          aria-hidden="true"
          @click.prevent="setToday"
        >
          {{ todayButtonText }}
        </button>
      </div>
    </div>

    <datepicker
      v-if="datePickerVisible"
      ref="datePicker"
      :class="{ 'vdp-datepicker__left': leftSide, 'vdp-datepicker__top': topSide }"
      :input-class="{ 'form-control': true, borderless: borderless, ...borderValidationClass, 'd-none': true }"
      :placeholder="datepicker.options.placeholder"
      :language="datepicker.language"
      :format="datepicker.options.format"
      :value="model.$model"
      :clear-button="datepicker.options.clearButton"
      :disabled="disabled"
      :disabled-dates="getDisabledDates"
      :full-month-name="true"
      :open-date="openDate"
      @input="dateChanged"
      @focus="onFocus($event)"
      @blur="pickerBlur($event)"
      @closed="pickerBlur($event)"
    />

    <w-error
      :model="model"
      :err-id="errId"
      @focus="datePickerVisible ? $refs.input.focus() : $refs.datePicker.focus()"
    />
  </div>
</template>

<script>
import { schemaMethods } from '@witkac/jschema';
import pl from 'vuejs-datepicker/dist/locale/translations/pl';
import Datepicker from 'vuejs-datepicker/dist/vuejs-datepicker.esm.js';

import modelMixin from '../mixins/modelMixin';
import { errorMixin } from '../mixins/errorMixin';
import WError from './error.vue';
import WTooltip from './tooltip.vue';

export default {
  name: 'WDatepicker',
  components: { WError, Datepicker, WTooltip },
  mixins: [modelMixin, errorMixin('dtp')],
  props: {
    placeholder: String,
    dateOptions: Object,
    disabledDates: Object,
    position: { type: String, default: 'bottom-right' },
    titleAttribute: String,
    // Czy pokazywać przycisk ustawiający dzisiejszą datę
    todayButton: { type: Boolean, default: false },
    // Tekst przycisku ustawiającego dzisiejszą datę - default: "Dzisiaj"
    todayButtonText: { type: String, default: 'Dzisiaj' }
  },
  data() {
    const dateOpts = {
      placeholder: this.placeholder || '',
      format: 'dd.MM.yyyy',
      disableDatesFromWarnings: false
    };
    return {
      datePickerVisible: false,
      datepicker: {
        language: pl,
        value: '',
        options: { ...dateOpts, ...this.dateOptions }
      }
    };
  },
  computed: {
    descriptionId() {
      return 'desc_' + this.sid;
    },
    leftSide() {
      return this.position.includes('left');
    },
    topSide() {
      return this.position.includes('top');
    },
    getDisabledDates() {
      let disabledDates = this.disabledDates;
      const $schema = this.model.$schema;
      const warnings = this.datepicker.options.disableDatesFromWarnings && $schema.warnings;

      if (disabledDates === undefined) {
        disabledDates = {};
      }

      if ($schema.exclusiveMinimum || (warnings && warnings.exclusiveMinimum)) {
        disabledDates.to = schemaMethods.dateParse(
          ($schema.exclusiveMinimum || (warnings && warnings.exclusiveMinimum)) + ' 23:59:59'
        );
      }
      if ($schema.minimum || (warnings && warnings.minimum)) {
        disabledDates.to = schemaMethods.dateParse(($schema.minimum || (warnings && warnings.minimum)) + ' 00:00:00');
      }
      if ($schema.exclusiveMaximum || (warnings && warnings.exclusiveMaximum)) {
        disabledDates.from = schemaMethods.dateParse(
          ($schema.exclusiveMaximum || (warnings && warnings.exclusiveMaximum)) + ' 00:00:00'
        );
      }
      if ($schema.maximum || (warnings && warnings.maximum)) {
        disabledDates.from = schemaMethods.dateParse(($schema.maximum || (warnings && warnings.maximum)) + ' 23:59:59');
      }

      return disabledDates;
    },
    openDate: {
      get() {
        return this.getDisabledDates.to || new Date();
      }
    }
  },
  watch: {
    dateOptions(next, prev) {
      this.datepicker.options = { ...this.datepicker.options, ...next };
    },
    placeholder(next, prev) {
      this.$set(this.datepicker.options, 'placeholder', next);
    }
  },
  mounted() {
    this.model.$value = this.model.$value;
  },

  methods: {
    toggleDatePicker() {
      if (this.datePickerVisible) {
        this.hideDatePicker();
      } else {
        if (!this.model.$schema.readOnly) {
          this.showDatePicker();
        }
      }
    },

    showDatePicker() {
      this.datePickerVisible = true;
      const self = this;
      this.$nextTick(() => {
        self.$refs.datePicker.$el.children[0].children[0].focus();
        self.$refs.datePicker.showCalendar();
        self.model.$hasFocus = true;
      });
    },

    hideDatePicker() {
      this.datePickerVisible = false;
      const self = this;

      this.$nextTick(() => {
        self.model.$hasFocus = false;
      });
    },

    pickerBlur($event) {
      this.hideDatePicker();
    },

    dateChanged(newValue) {
      const tnewvalue = newValue.getTime();

      if (this.localValue === undefined || tnewvalue !== this.localValue) {
        this.localValue = tnewvalue;
        const stringValue = schemaMethods.fdatetime(newValue, 'dd.MM.yyyy');
        this.$emit('input', stringValue);
        this.fieldChanged();

        if (!this.$options.vmodel) {
          const nvalue = schemaMethods.fdate(newValue);
          this.model.$model = nvalue;
          this.model.$hasFocus = false;
        }
      }
    },
    // @vuese
    // Ustawienie dzisiejszej daty
    setToday() {
      this.dateChanged(new Date());
    },
    clear() {
      this.model.$model = '';
      this.$emit('input', '');
    }
  }
};
</script>

<style>
.vdp-datepicker__left > div:not(:first-child) {
  right: 0%;
}
.vdp-datepicker__top > div:not(:first-child) {
  bottom: 100%;
}
.vdp-datepicker__calendar .cell.selected {
  color: #fff;
  background-color: #a90329;
}
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover,
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
  background-color: #428bca;
  color: white;
}
.vdp-datepicker__calendar header {
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
}
.vdp-datepicker__calendar .cell {
  border: 1px solid transparent !important;
}
.vdp-datepicker__calendar .cell.selected::before,
.vdp-datepicker__calendar .cell.selected::after {
  content: none;
}

.datepicker .input {
  position: relative;
}

.input .append {
  position: absolute;
  right: 0;
  top: 0;
}

.vdp-datepicker {
  flex: 1 1 auto;
  /* width: 100%; */
}
</style>
